/**
 * Parcelas em aberto de um inadimplente do Servopa.
 *
 * POR QUE este arquivo existe, e não uma conta solta na tela:
 *
 * O relatório de inadimplência do Servopa traz uma coluna dupla "PGO ATR" —
 * quantas parcelas o consorciado PAGOU e quantas estão em ATRASO. O parser
 * (`api/api-server/servopa/src/parse-inadimplentes.mjs`) capturou os dois
 * números na ordem certa, mas batizou o segundo de `dias_atraso`. Não são
 * dias. Conferindo a base de DEV no dia 2026-09-06:
 *
 *   - `pgo_atr` bate EXATAMENTE com `parcelas_pagas` de
 *     `servopa_vendas_with_details` (15/15 contratos do último sync);
 *   - `dias_atraso` bate com `round(valor_atraso / valor_parcela)`, também
 *     15/15 — ou seja, é a QUANTIDADE de parcelas que compõem o valor em
 *     atraso;
 *   - em 180 syncs `dias_atraso` nunca passou de 3. Coluna de dias de atraso
 *     de consórcio viveria em 30/60/90; de parcelas vencidas, não.
 *
 * Então o dado que o cliente pediu ("quantas parcelas estão em aberto") JÁ
 * EXISTE. O que faltava era chamá-lo pelo nome certo, e a migration 165 fez
 * isso no banco: a coluna passou a se chamar `parcelas_em_aberto`. A antiga
 * continua sendo lida aqui como reserva porque a remoção dela (migration 167)
 * espera o parser novo chegar aos servidores — enquanto isso, uma linha pode
 * ter vindo por qualquer um dos dois caminhos. Quando a 167 rodar, o campo
 * `dias_atraso` desaparece e esta reserva vira código morto, para ser apagada.
 *
 * O QUE NÃO DÁ PARA CALCULAR: quantas parcelas faltam até o fim do plano. Nem
 * `servopa_inadimplentes` nem `servopa_vendas_with_details` guardam o prazo do
 * grupo, então "prazo − pagas" não tem de onde sair. Numa tela de cobrança um
 * número inventado é pior que um campo ausente, então este módulo devolve
 * apenas o que a fonte sustenta.
 */

/** Campos do registro de inadimplente que interessam para a conta. */
export interface RegistroInadimplente {
  /** Coluna PGO do relatório: parcelas já pagas. */
  pgo_atr?: number | string | null;
  /** Coluna ATR do relatório: parcelas vencidas em aberto. Nome atual (migration 165). */
  parcelas_em_aberto?: number | string | null;
  /** Nome antigo da mesma coluna, mantido só durante a transição. Ver o cabeçalho. */
  dias_atraso?: number | string | null;
  valor_atraso?: number | string | null;
}

export interface ParcelasDoInadimplente {
  /** Parcelas vencidas e ainda não pagas. `null` quando o Servopa não mandou o número. */
  emAberto: number | null;
  /** Parcelas já quitadas até aqui. `null` quando o Servopa não mandou o número. */
  pagas: number | null;
  /** Quanto, em média, cada parcela em aberto representa do valor devido. */
  valorMedioPorParcela: number | null;
}

/**
 * PostgREST devolve `numeric` como string ("3094.50"), e o scraper grava
 * string vazia quando a linha do relatório não casou com o regex. Sem esta
 * normalização `Number("")` viraria 0 e a tela mostraria "0 parcelas em
 * aberto" para um contrato do qual simplesmente não sabemos nada.
 */
const paraNumeroNaoNegativo = (valor: number | string | null | undefined): number | null => {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === "string" && valor.trim() === "") return null;
  const numero = Number(valor);
  if (!Number.isFinite(numero) || numero < 0) return null;
  return numero;
};

/**
 * Lê do registro o que dá para afirmar sobre as parcelas.
 *
 * Não estima nada: cada campo ou vem da fonte ou volta `null`.
 */
export const calcularParcelasEmAberto = (
  registro: RegistroInadimplente | null | undefined,
): ParcelasDoInadimplente => {
  // Nome novo primeiro; `??` e não `||` porque 0 parcelas em aberto é um valor
  // legítimo e cairia para a coluna velha se o teste fosse de veracidade.
  const emAberto = paraNumeroNaoNegativo(registro?.parcelas_em_aberto ?? registro?.dias_atraso);
  const pagas = paraNumeroNaoNegativo(registro?.pgo_atr);
  const valorAtraso = paraNumeroNaoNegativo(registro?.valor_atraso);

  return {
    emAberto: emAberto === null ? null : Math.trunc(emAberto),
    pagas: pagas === null ? null : Math.trunc(pagas),
    // Dividir por zero parcela não é "R$ 0", é uma pergunta sem resposta.
    valorMedioPorParcela:
      valorAtraso !== null && emAberto !== null && emAberto > 0 ? valorAtraso / emAberto : null,
  };
};

/**
 * Rótulo da tela. Diz o que o número significa em vez de só cuspir o número —
 * quem cobra precisa saber se "2" são parcelas, dias ou reais.
 */
export const rotuloParcelasEmAberto = (parcelas: ParcelasDoInadimplente): string => {
  if (parcelas.emAberto === null) return "Servopa não informou";
  if (parcelas.emAberto === 1) return "1 parcela vencida e não paga";
  return `${parcelas.emAberto} parcelas vencidas e não pagas`;
};
